import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { SVGImageComponent } from '@app/components/svg-image/svg-image.component';
import { AnalyticEvents, FeatureFlag, SocketEventTypes } from '@app/enums';
import { PlatformModule } from '@app/platform.module';
import { AnalyticService, AuthService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { JobPostedEventPayload } from '@applicant-tracker/interfaces/job-posted-event-payload.interface';
import { ViolationItem } from '@applicant-tracker/interfaces/validation-response.interface';
import { ApplicantTrackerFormsModule } from '@forms/applicant-tracker/applicant-tracker-forms.module';
import { ATSJobIntegration } from '@models/company/ats-job-integration.model';
import { JobIndeedIntegration } from '@models/company/job-indeed-integration.model';
import { IntegrationLinkedin } from '@models/company/settings/integrations/linkedin.model';
import { SettingsIntegration } from '@models/company/settings/settings-integration.model';
import { Budget } from '@models/recruiting/indeed/budget.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { TranslateModule } from '@ngx-translate/core';
import { ActiveIntegrations } from '@settings/enums/integration.enum';
import { SettingsIntegrationService } from '@settings/services';
import { from, Subscription } from 'rxjs';

type Meta = {
    syncing: boolean;
    failed: boolean;
    publicUrl?: string;
    budget?: Budget;
    logo: string;
    disabled?: boolean;
};

type IntegrationObject = {
    atsJobIntegration: ATSJobIntegration;
    meta: Meta;
};

@Component({
    selector: 'app-job-board-integrations-v2',
    templateUrl: './job-board-integrations-v2.template.html',
    styleUrls: ['./job-board-integrations-v2.style.scss'],
    standalone: true,
    imports: [
        CommonModule,
        PlatformModule,
        FormsModule,
        TranslateModule,
        RouterLink,
        ApplicantTrackerFormsModule,
        SVGImageComponent,
    ],
})
export class JobBoardIntegrationsV2Component implements OnInit {
    @ViewChild('integrationConfirmationDialog', { static: true })
    integrationConfirmationDialog!: ConfirmationDialogComponent;
    @ViewChild('unableToSponsorDialog', { static: true })
    unableToSponsorDialog!: ConfirmationDialogComponent;
    @ViewChild('sponsorDialog', { static: true })
    sponsorDialog!: ConfirmationDialogComponent;
    @ViewChild('indeedValidationDialog', { static: true })
    indeedValidationDialog!: ConfirmationDialogComponent;

    @Input() jobPosting: JobPosting;

    isLoading = false;
    integrations: SettingsIntegration[] = [];
    activeJobIntegrations: ATSJobIntegration[] = [];
    integrationObjects: IntegrationObject[] = [];
    activeIntegrationObject?: IntegrationObject;
    activeIntegrations = ActiveIntegrations;
    indeedValidationMessages: ViolationItem[] = [];

    shouldShowExploreIntegrations = false;
    linkedinXmlIntegration = false;

    isIndeedDisabled = toSignal(from(this.featureService.has('disable-indeed-integration-ui')));

    private socketSubscription = Subscription.EMPTY;

    /**
     * This holds indeed specific data about the job posting, like the ID of the job posting on indeed.
     * This data is created after the queue posts the job to indeed.
     */
    private atsJobIndeedIntegration: JobIndeedIntegration;

    /**
     * Holds company level config details for integration. Ideally these related tables should be returned in
     * the same request as integrations, but our BE doesnt support them as an include.
     */
    private integrationDetailCache: { [key: string]: IntegrationLinkedin } = {};

    constructor(
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private integrationService: SettingsIntegrationService,
        private socketService: SocketService,
        private auth: AuthService,
        private destroyRef: DestroyRef,
        private featureService: FeatureService
    ) {
        this.integrationService.jobPostingStateChange$.pipe(takeUntilDestroyed()).subscribe((state) => {
            this.loadIntegrations();

            if (state === 'archived') {
                this.atsJobIndeedIntegration = undefined;
            }
        });
    }

    async ngOnInit(): Promise<void> {
        this.linkedinXmlIntegration = await this.featureService.has(FeatureFlag.linkedinXmlIntegration);
        this.atsJobIndeedIntegration = this.jobPosting.indeedIntegration;
        await this.loadIntegrations();
    }

    async updateIntegration(state: boolean, integrationObject: IntegrationObject): Promise<void> {
        if (state) {
            await this.openAddIntegrationModal(integrationObject);
            return;
        }

        this.openRemoveIntegrationModal(integrationObject);
    }

    openLink(link?: string): void {
        if (link) {
            window.open(link);
        }
    }

    private async openRemoveIntegrationModal(integrationObject: IntegrationObject): Promise<void> {
        this.activeIntegrationObject = integrationObject;
        const confirm = await this.integrationConfirmationDialog.open();

        if (!confirm) {
            integrationObject.atsJobIntegration.isEnabled = true;
            return;
        }

        this.analyticService.trackEvent(AnalyticEvents.RecruitingRemovedIntegrationJob);

        this.isLoading = true;

        try {
            await integrationObject.atsJobIntegration.delete();

            if (integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.indeed)) {
                this.integrationService.jobSyncedWithIndeed$.next(null);
            }
        } catch (err) {
            this.notify.error('applicant-tracker.job-board-integrations-card-v2.unableToDelete');
        } finally {
            await this.loadIntegrations();
            this.isLoading = false;
        }
    }

    private async openAddIntegrationModal(integrationObject: IntegrationObject): Promise<void> {
        this.activeIntegrationObject = integrationObject;
        const confirm = await this.integrationConfirmationDialog.open();

        if (!confirm) {
            integrationObject.atsJobIntegration.isEnabled = false;
            return;
        }

        this.analyticService.trackEvent(AnalyticEvents.RecruitingAddedIntegrationJob);

        this.isLoading = true;

        if (integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.indeed)) {
            this.subscribeToSyncChanges(integrationObject);
        }

        try {
            await integrationObject.atsJobIntegration.save();

            if (integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.indeed)) {
                integrationObject.meta.syncing = true;
            }

            if (integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.linkedin)) {
                integrationObject.meta.publicUrl = await this.getLinkedinPublicUrl(integrationObject);
            }
        } catch (err) {
            if (integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.indeed)) {
                integrationObject.meta.syncing = false;
            }
            this.notify.error('applicant-tracker.job-board-integrations-card-v2.unableToSave');
        } finally {
            this.isLoading = false;
        }
    }

    private async loadIntegrations(): Promise<void> {
        this.isLoading = true;

        const [integrations, activeJobIntegrations] = await Promise.all([
            this.integrationService.getCompanyJobBoardRelatedIntegrations(),
            this.integrationService.getJobIntegrations(this.jobPosting.id),
        ]);

        this.integrations = integrations;
        this.activeJobIntegrations = activeJobIntegrations;

        this.integrationObjects = [];
        this.integrations.forEach((integration: SettingsIntegration) => {
            if (!integration.isEnabled) {
                return;
            }

            let jobIntegration = this.activeJobIntegrations.find((activeJobIntegration: ATSJobIntegration) => {
                return activeJobIntegration.integrationId === integration.id;
            });

            if (!jobIntegration) {
                jobIntegration = new ATSJobIntegration({
                    atsJobId: this.jobPosting.id,
                    integrationId: integration.id,
                    isEnabled: false,
                });
            }

            jobIntegration.integrationType = integration;

            // Indeed: Allow toggle on, disallow toggle off
            let isDisabled = false;
            if (integration.integrationType.name === ActiveIntegrations.indeed) {
                if (this.isIndeedDisabled()) {
                    isDisabled = true;
                    jobIntegration.isEnabled = false;
                } else if (jobIntegration.isEnabled) {
                    isDisabled = true;
                }
            }

            this.integrationObjects.push({
                atsJobIntegration: jobIntegration,
                meta: {
                    syncing: false,
                    failed: false,
                    disabled: isDisabled,
                    logo: `${integration.integrationType.assetBaseURL}${integration.integrationType.name}.svg`,
                },
            });
        });

        this.shouldShowExploreIntegrations = this.linkedinXmlIntegration && this.integrationObjects.length === 1;

        const indeedIntegrationObject: IntegrationObject | undefined = this.integrationObjects.find(
            (integrationObject: IntegrationObject) =>
                integrationObject.atsJobIntegration.isPersisted &&
                integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.indeed)
        );

        if (indeedIntegrationObject) {
            // The atsJobIntegration is enabled when it's turned on by the user.
            // If it's enabled but we dont yet have a value for atsJobIndeedIntegration, it means
            // we haven't completed syncing yet.
            indeedIntegrationObject.meta.syncing =
                indeedIntegrationObject.atsJobIntegration.isEnabled && !this.atsJobIndeedIntegration;

            if (indeedIntegrationObject.meta.syncing) {
                this.subscribeToSyncChanges(indeedIntegrationObject);
            } else {
                indeedIntegrationObject.meta.publicUrl = this.getIndeedUrl(this.atsJobIndeedIntegration.employerJobId);
            }
        }

        const linkedinIntegrationObject: IntegrationObject | undefined = this.integrationObjects.find(
            (integrationObject: IntegrationObject) =>
                integrationObject.atsJobIntegration.isPersisted &&
                integrationObject.atsJobIntegration.isIntegrationOfType(ActiveIntegrations.linkedin)
        );

        if (linkedinIntegrationObject) {
            linkedinIntegrationObject.meta.publicUrl = await this.getLinkedinPublicUrl(linkedinIntegrationObject);
        }

        this.isLoading = false;
    }

    private async getLinkedinPublicUrl(integrationObject: IntegrationObject): Promise<string> {
        const integrationDetailId = integrationObject.atsJobIntegration.integrationType.integrationDetailId;

        if (!this.integrationDetailCache[integrationDetailId]) {
            let linkedinIntegration: IntegrationLinkedin;

            try {
                linkedinIntegration = await IntegrationLinkedin.param('company', this.auth.company.id).find(
                    integrationDetailId
                );
            } catch (e) {
                return '';
            }

            this.integrationDetailCache[integrationDetailId] = linkedinIntegration;
        }

        return `https://www.linkedin.com/jobs/search/?f_C=${this.integrationDetailCache[integrationDetailId].linkedinCompanyId}`;
    }

    private getIndeedUrl(employerJobId: string | null): string {
        if (!employerJobId) {
            return '';
        }

        // TODO Indeed, new integration
        //  Not guaranteed to work, instead use GraphQL and get managementUrls.viewJob with the node query

        return `https://employers.indeed.com/jobs/view?employerJobId=${employerJobId}`;
    }

    /**
     * Only relevant to indeed at the moment because posting to indeed isn't instant.
     */
    private subscribeToSyncChanges(integrationObject: IntegrationObject): void {
        if (!this.socketSubscription.closed) {
            return;
        }

        this.socketSubscription = this.socketService
            .getCompanyChannel([SocketEventTypes.jobPostedOnIndeed, SocketEventTypes.jobPostingFailedOnIndeed])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (payload: JobPostedEventPayload) => {
                if (payload.jobPostId !== this.jobPosting.id) {
                    return;
                }

                if (!payload.status) {
                    integrationObject.meta.failed = true;
                    this.socketSubscription.unsubscribe();
                    return;
                }

                integrationObject.meta.syncing = false;
                integrationObject.meta.publicUrl = payload.publicUrl;
                integrationObject.meta.disabled = true;

                const jobPosting = await JobPosting.param('company', this.auth.company.id)
                    .with(['indeedIntegration', 'jobIntegrations.integration.integrationType'])
                    .find(this.jobPosting.id);

                this.atsJobIndeedIntegration = jobPosting.indeedIntegration;
                this.integrationService.jobSyncedWithIndeed$.next(this.atsJobIndeedIntegration);

                this.socketSubscription.unsubscribe();
            });
    }
}
